import * as R from "ramda";

const DECIMAL_PATTERNS = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
const ROMAN_PATTERNS = [
  "M",
  "CM",
  "D",
  "CD",
  "C",
  "XC",
  "L",
  "XL",
  "X",
  "IX",
  "V",
  "IV",
  "I"
];

const pagesCount = window.getClientConfiguration(0, ["book", "pagesCount"]);
const offset = window.getClientConfiguration(0, ["book", "pageOffset"]);
const pageNames = window.getClientConfiguration({}, ["book", "pageNames"]);
const interjacentPageNames = window.getClientConfiguration(false, [
  "book",
  "interjacentPageNames"
]);

const preOffsetNumbering = window.getClientConfiguration("roman", [
  "book",
  "preOffsetNumbering"
]);

const toFormat = getToFormatter(preOffsetNumbering);

const pagesLookup = (() => {
  const pages = R.range(1, pagesCount + 1)
    .map((page, index) => {
      if (index < offset) {
        return toFormat(page);
      }

      return page - offset;
    })
    .map((page, index) => {
      if (R.has(index, pageNames)) {
        const alias = R.prop(index, pageNames);

        if (interjacentPageNames) {
          return [alias, page];
        }

        return alias;
      }

      return page;
    });

  return R.take(pagesCount, R.flatten(pages));
})();

export function absolutePageToRelative(page) {
  return R.prop(page - 1, pagesLookup);
}

export function relativePageToAbsoluteNumerical(page) {
  return page + offset;
}

export function absolutePageToRelativeNumerical(page) {
  return page - offset;
}

export function relativePageToAbsolute(page) {
  return (
    R.findIndex(pageLookup => {
      return (
        String(pageLookup)
          .trim()
          .toLowerCase() ===
        String(page)
          .trim()
          .toLowerCase()
      );
    }, pagesLookup) + 1
  );
}

export function toRoman(decimalNumeral) {
  let number = decimalNumeral;
  let result = "";

  for (let i = 0; i <= DECIMAL_PATTERNS.length; i += 1) {
    while (number % DECIMAL_PATTERNS[i] < number) {
      result += ROMAN_PATTERNS[i];
      number -= DECIMAL_PATTERNS[i];
    }
  }

  return result;
}

export function fromRoman(romanNumeral) {
  let value = romanNumeral;
  let result = 0;

  for (let i = 0; i <= DECIMAL_PATTERNS.length; i += 1) {
    while (value.indexOf(ROMAN_PATTERNS[i]) === 0) {
      result += DECIMAL_PATTERNS[i];
      value = value.replace(ROMAN_PATTERNS[i], "");
    }
  }

  return result;
}

// converts a number from base 10 to "base 26"
// note: in this case the numerical values of base 26 get represented by letters only
// e.g.    toLetter(1) ===   "A"
// e.g.   toLetter(10) ===   "J"
// e.g.  toLetter(100) ===  "CV"
// e.g. toLetter(1000) === "ALL"
export function toLetter(numerical, isUpper = true) {
  const start = isUpper ? "A" : "a";
  const end = isUpper ? "Z" : "z";
  const ordA = start.charCodeAt(0);
  const ordZ = end.charCodeAt(0);
  const ordRange = ordZ - ordA + 1;

  let letters = [];
  let remainder = numerical - 1;

  while (remainder >= 0) {
    letters.unshift(String.fromCharCode((remainder % ordRange) + ordA));
    remainder = Math.floor(remainder / ordRange) - 1;
  }

  return letters.join("");
}

export function toLetterLower(numerical) {
  return toLetter(numerical, false);
}

export function toLetterUpper(numerical) {
  return toLetter(numerical, true);
}

// converts a number from "base 26" to base 10
// note: in this case the numerical values of base 26 get represented by letters only
// e.g.    fromLetter("A") ===     1
// e.g.   fromLetter("AA") ===    27
// e.g.  fromLetter("AAA") ===   703
// e.g. fromLetter("AAAA") === 18279
export function fromLetter(letters, isUpper = true) {
  const start = isUpper ? "A" : "a";
  const end = isUpper ? "Z" : "z";
  const ordA = start.charCodeAt(0);
  const ordZ = end.charCodeAt(0);
  const ordRange = ordZ - ordA + 1;

  return letters
    .toUpperCase()
    .split("")
    .reverse()
    .map((letter, idx) => {
      // letter to digit
      // e.g. A -> 1, B -> 2
      const letterValue = letter.charCodeAt(0) - ordA + 1;
      // digit to positional value (like a positional numeral system)
      // e.g. binary: 0100 === (0 * 2 ** 0) + (1 * 2 ** 2) + (0 * 2 ** 1) + (0 * 2 ** 3)
      const positionalValue = letterValue * ordRange ** idx;
      return positionalValue;
    })
    .reduce((sum, value) => sum + value, 0);
}

export function fromLetterLower(numerical) {
  return fromLetter(numerical, false);
}

export function fromLetterUpper(numerical) {
  return fromLetter(numerical, true);
}

export function getToFormatter(formatType) {
  switch (formatType) {
    case "roman":
      return toRoman;
    case "lowerletter":
      return toLetterLower;
    case "upperletter":
      return toLetterUpper;
    default:
      return R.identity;
  }
}

export function getFromFormatter(formatType) {
  switch (formatType) {
    case "roman":
      return fromRoman;
    case "lowerletter":
      return fromLetterLower;
    case "upperletter":
      return fromLetterUpper;
    default:
      return R.identity;
  }
}
